// Client-side photo compression so site photos stay well under Brevo's 4 MB
// email attachment cap even from modern phone cameras.
export const compressImage = (file, maxDim = 1600, quality = 0.8) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the photo — try again"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not process the photo — try again"));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

// ---------------------------------------------------------------------------
// Smart compression for high-volume galleries (Client Update reports)
// ---------------------------------------------------------------------------

export const SMART_MAX_DIM = 1200; // px — longest edge; plenty for an A4 grid cell and keeps the PDF sane
export const SMART_TARGET_BYTES = 450 * 1024; // aim for 300–500 KB per photo
const QUALITY_LADDER = [0.86, 0.8, 0.74, 0.68, 0.62, 0.56, 0.5];
const DIM_LADDER = [SMART_MAX_DIM, 1000, 800];

const isImageFile = (file) =>
  Boolean(file) && (String(file.type || "").startsWith("image/") || /\.(jpe?g|png|webp|heic|heif|gif|bmp|avif)$/i.test(file.name || ""));

/** Decode a File into a drawable bitmap — EXIF-upright via createImageBitmap when the browser supports it. */
const decodeImage = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // fall through to the <img> path (older Safari, unsupported codecs)
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Could not decode ${file.name || "that photo"}`));
      img.src = url;
    });
  } finally {
    // revoke after decode completes; the drawn canvas no longer needs the URL
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
};

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not encode the photo"))), "image/jpeg", quality);
  });

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the compressed photo"));
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

/**
 * Resize to ≤ `maxDim` px on the longest edge, then step JPEG quality down until the encoded photo is
 * ≤ `targetBytes` (falling back to smaller dimensions for very detailed shots). Returns
 * { dataUrl, bytes, width, height, quality, originalBytes }.
 */
export const smartCompressImage = async (file, { maxDim = SMART_MAX_DIM, targetBytes = SMART_TARGET_BYTES } = {}) => {
  if (!isImageFile(file)) throw new Error(`${file?.name || "That file"} isn't an image`);
  const source = await decodeImage(file);
  const srcW = source.width || source.naturalWidth;
  const srcH = source.height || source.naturalHeight;
  if (!srcW || !srcH) throw new Error(`Could not read ${file.name || "that photo"}`);

  let best = null;
  const dims = DIM_LADDER.filter((d) => d <= maxDim);
  if (!dims.length || dims[0] !== maxDim) dims.unshift(maxDim);
  for (const dim of dims) {
    const scale = Math.min(1, dim / Math.max(srcW, srcH));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(srcW * scale));
    canvas.height = Math.max(1, Math.round(srcH * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF"; // flatten transparency (PNG / HEIC alpha) onto white for JPEG
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    for (const quality of QUALITY_LADDER) {
      const blob = await canvasToBlob(canvas, quality); // eslint-disable-line no-await-in-loop
      if (!best || blob.size < best.blob.size) best = { blob, width: canvas.width, height: canvas.height, quality };
      if (blob.size <= targetBytes) {
        best = { blob, width: canvas.width, height: canvas.height, quality };
        break;
      }
    }
    if (best && best.blob.size <= targetBytes) break;
  }
  if (typeof source.close === "function") source.close();
  const dataUrl = await blobToDataUrl(best.blob);
  return { dataUrl, bytes: best.blob.size, width: best.width, height: best.height, quality: best.quality, originalBytes: file.size || 0 };
};

/**
 * Compress many files with bounded concurrency (phones choke on 50 canvases at once).
 * `onProgress({ done, total })` fires after each file. Resolves { results: [{ file, ...compressed } | null], failed: [file] }
 * in the original order — a bad file never sinks the batch.
 */
export const compressMany = async (files, { concurrency = 3, onProgress, ...opts } = {}) => {
  const list = Array.from(files || []);
  const results = new Array(list.length).fill(null);
  const failed = [];
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < list.length) {
      const i = next++;
      try {
        results[i] = { file: list[i], ...(await smartCompressImage(list[i], opts)) }; // eslint-disable-line no-await-in-loop
      } catch {
        failed.push(list[i]);
      }
      done += 1;
      onProgress?.({ done, total: list.length });
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, list.length) }, worker));
  return { results, failed };
};

export const fmtKB = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
